export type CalmNavigation = {routeKey:string;path:string;label:string;description?:string|null};
export type CalmResult = {title:string;note:string;items:CalmNavigation[]};

const routeNotes:Record<string,string>={
  assurance:'등록된 근거를 확인하는 화면을 엽니다. 실시간 검색이나 올해 변경점 확인을 뜻하지는 않아요.',
  operations:'학교의 기존 운영 화면을 엽니다. 계획을 자동으로 확정하거나 담당자를 배정하지 않아요.',
  learners:'학생 목록에서 대상을 선택해 주세요. 아직 특정 학생을 조회한 결과는 아니에요.',
  classes:'기존 학급·상담 화면에서 학급과 학생을 확인해 주세요.',
  assessments:'검사 운영 화면에서 검사와 대상 학생을 확인해 주세요.',
  programs:'수업·체험 운영 화면에서 활동과 대상 학급을 선택해 주세요.',
  partners:'기존 체험처·협력기관 화면을 엽니다. 지원 신청이나 접수는 아직 하지 않았어요.',
  reports:'보고서 화면을 엽니다. 보고서 생성이나 제출은 아직 하지 않았어요.',
  badges:'배지 화면에서 대상과 발급 기준을 확인해 주세요. 아직 발급하지 않았어요.',
  evidence:'활동 근거를 검토하는 화면을 엽니다. 검토 상태는 바꾸지 않아요.',
  'student-lifecycle':'학적 관리 화면에서 대상과 변경 내용을 확인해 주세요.',
  roster:'학생 명단·반편성 화면을 엽니다. 기존 명단은 바꾸지 않아요.',
  today:'기존 오늘의 업무 화면을 엽니다.',
  privacy:'기존 동의·정보보호 화면에서 대상을 확인해 주세요.',
  help:'사용안내와 자주 묻는 질문을 엽니다.'
};
const rules:Array<[RegExp,string[],string]>=[
  [/지침|공문|정책|공지|공고|바뀐|교육청|교육부|(?:올해|내년).*(?:준비|자료)|^(?:올해|내년)$/,['assurance'],'올해 자료부터 살펴볼까요?'],
  [/연간|학교\s*계획|계획.*(?:초안|작성)|업무분장|학교\s*운영/,['operations'],'학교 계획을 이어갈까요?'],
  [/도움받|기관|체험처|협력|지원받/,['partners'],'도움받을 곳을 확인할까요?'],
  [/배지|발급|스탬프/,['badges'],'대상과 기준부터 확인할까요?'],
  [/검사|심리|적성/,['assessments'],'검사 운영으로 이어갈까요?'],
  [/보고|결과서|통계|기록.*작성/,['reports'],'기록과 보고를 이어갈까요?'],
  [/증빙|증거|근거.*검토/,['evidence'],'활동 근거를 살펴볼까요?'],
  [/전학|전입|전출|졸업|학년.*이동|학적/,['student-lifecycle'],'학적 변경을 준비할까요?'],
  [/반편성|명단|엑셀|CSV/i,['roster'],'학생 명단을 확인할까요?'],
  [/상담|학급|[0-9]+학년.*[0-9]+반/,['classes'],'학급과 상담을 이어갈까요?'],
  [/체험|수업|프로그램|활동/,['programs'],'수업과 체험을 준비할까요?'],
  [/학생|성장기록/,['learners'],'학생을 찾아볼까요?'],
  [/오늘|할\s*일/,['today'],'오늘의 업무를 확인할까요?'],
  [/동의|개인정보/,['privacy'],'동의 현황을 확인할까요?'],
  [/도움말|사용법|사용안내/,['help'],'사용안내를 살펴볼까요?']
];

/** Paths are navigation targets, never model-generated URLs or executable commands. */
export function safePath(path:string):boolean{
  return /^\/(?:[a-zA-Z0-9_-]+\/)*[a-zA-Z0-9_-]*$/.test(path);
}
export function availableNavigation(items:readonly CalmNavigation[]):CalmNavigation[]{
  const seen=new Set<string>();
  return items.filter(item=>{
    if(!item || typeof item.routeKey!=='string' || typeof item.path!=='string' || !safePath(item.path) || seen.has(item.path))return false;
    seen.add(item.path);return true;
  });
}
export function canNavigate(path:string,items:readonly CalmNavigation[]):boolean{
  return safePath(path)&&availableNavigation(items).some(item=>item.path===path);
}
export function resolveCalmRequest(query:string,items:readonly CalmNavigation[]):CalmResult{
  const text=query.normalize('NFKC').trim().slice(0,500);
  const allowed=availableNavigation(items);
  if(!text)return {title:'',note:'',items:[]};
  for(const [pattern,keys,title] of rules){
    if(pattern.test(text)){
      const found=keys.flatMap(key=>allowed.filter(item=>item.routeKey===key));
      return found.length?{title,note:routeNotes[found[0].routeKey]||'기존 업무 화면에서 이어갈 수 있어요.',items:found}:
        {title:'이 업무는 연결을 확인해야 해요.',note:'현재 학교의 메뉴에서 연결 대상을 확인하지 못했어요. 기존 화면에서 확인해 주세요.',items:[]};
    }
  }
  const found=allowed.filter(item=>item.label.toLocaleLowerCase().includes(text.toLocaleLowerCase())).slice(0,3);
  return found.length?{title:'이 업무로 이어갈 수 있어요.',note:'입력 내용으로 조회·수정·발송을 실행하지는 않았어요.',items:found}:
    {title:'어떤 업무인지 조금만 알려주세요.',note:'예를 들어 ‘학생 찾기’, ‘검사 운영’, ‘올해 자료’라고 적어주세요.',items:[]};
}
export function isCalmEntry(path:string,items:readonly CalmNavigation[]):boolean{
  return path==='/' || availableNavigation(items).some(item=>item.routeKey==='today'&&item.path===path);
}
export function contextMatches(expected:{accountId:string;personId:string;schoolId:string},actual:{accountId:string;personId:string;schoolId:string;allowed:boolean}):boolean{
  return Boolean(expected.accountId&&expected.personId&&expected.schoolId&&actual.allowed&&
    expected.accountId===actual.accountId&&expected.personId===actual.personId&&expected.schoolId===actual.schoolId);
}
